import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Row,
  Col,
  Card,
  Typography,
  Divider
} from 'antd';
import { CalendarOutlined, UserOutlined, FileTextOutlined } from '@ant-design/icons';
import apiService from "services/api/apiService";
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface LeaveRequestFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  editingRequest?: any;
}

const LeaveRequestForm: React.FC<LeaveRequestFormProps> = ({
  visible,
  onCancel,
  onSuccess,
  editingRequest
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [totalDays, setTotalDays] = useState(0);

  useEffect(() => {
    if (visible) {
      fetchEmployees();
      if (editingRequest) {
        // Populate form with editing data
        form.setFieldsValue({
          employee_id: editingRequest.employee_id,
          leave_type: editingRequest.leave_type,
          dateRange: [dayjs(editingRequest.start_date), dayjs(editingRequest.end_date)],
          reason: editingRequest.reason,
        });
        
        // Calculate total days for editing
        const start = dayjs(editingRequest.start_date);
        const end = dayjs(editingRequest.end_date);
        setTotalDays(end.diff(start, 'day') + 1);
      } else {
        form.resetFields();
        setTotalDays(0);
      }
    }
  }, [visible, editingRequest, form]);

  const fetchEmployees = async () => {
    try {
      console.log('🔄 Fetching employees for form...');
      const response = await apiService.getEmployees();
      console.log('👥 Employees response:', response);
      
      if (response.success) {
        let employeeData = response.data || [];
        
        if (!Array.isArray(employeeData)) {
          if (typeof employeeData === 'object' && employeeData.employees) {
            employeeData = employeeData.employees;
          } else if (typeof employeeData === 'object' && employeeData.data) {
            employeeData = employeeData.data;
          } else {
            employeeData = [];
          }
        }
        
        setEmployees(Array.isArray(employeeData) ? employeeData : []);
      }
    } catch (error) {
      console.error('💥 Error fetching employees:', error);
    }
  };

  const calculateDays = (dates: any) => {
    if (dates && dates.length === 2) {
      const start = dates[0];
      const end = dates[1];
      const days = end.diff(start, 'day') + 1;
      setTotalDays(days);
      return days;
    }
    setTotalDays(0);
    return 0;
  };

  const handleDateChange = (dates: any) => {
    calculateDays(dates);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      console.log('📝 Submitting leave request:', values);
      
      const formData = {
        employee_id: values.employee_id,
        leave_type: values.leave_type,
        start_date: values.dateRange[0].format('YYYY-MM-DD'),
        end_date: values.dateRange[1].format('YYYY-MM-DD'),
        days_requested: totalDays,
        reason: values.reason,
        status: 'pending',
      };

      console.log('📤 Sending form data:', formData);

      let response;
      if (editingRequest) {
        // Update existing request
        response = await apiService.updateLeaveRequest(editingRequest.id, formData);
        console.log('✏️ Update response:', response);
      } else {
        // Create new request
        response = await apiService.createLeaveRequest(formData);
        console.log('➕ Create response:', response);
      }

      if (response.success) {
        message.success(editingRequest ? 'Leave request updated successfully' : 'Leave request created successfully');
        form.resetFields();
        setTotalDays(0);
        onSuccess();
      } else {
        message.error(response.error || 'Failed to save leave request');
      }
    } catch (error) {
      console.error('💥 Submit error:', error);
      message.error('An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTotalDays(0);
    onCancel();
  };

  return (
    <Modal
      title={
        <div>
          <CalendarOutlined style={{ marginRight: 8 }} />
          {editingRequest ? 'Edit Leave Request' : 'New Leave Request'}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={editingRequest ? 'Update Request' : 'Submit Request'}
      cancelText="Cancel"
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '20px' }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employee_id"
              label={<><UserOutlined /> Employee</>}
              rules={[{ required: true, message: 'Please select an employee' }]}
            >
              <Select
                placeholder="Select employee"
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {employees.map((employee) => (
                  <Option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name} - {employee.employee_id}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="leave_type"
              label="Leave Type"
              rules={[{ required: true, message: 'Please select leave type' }]}
            >
              <Select placeholder="Select leave type">
                <Option value="annual">🏖️ Annual Leave</Option>
                <Option value="sick">🏥 Sick Leave</Option>
                <Option value="maternity">🤱 Maternity Leave</Option>
                <Option value="paternity">👨‍👶 Paternity Leave</Option>
                <Option value="emergency">🚨 Emergency Leave</Option>
                <Option value="unpaid">💸 Unpaid Leave</Option>
                <Option value="study">📚 Study Leave</Option>
                <Option value="bereavement">🖤 Bereavement Leave</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="dateRange"
              label="Leave Period"
              rules={[{ required: true, message: 'Please select leave dates' }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                onChange={handleDateChange}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Total Days">
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                  {totalDays} days
                </Text>
              </Card>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="reason"
          label={<><FileTextOutlined /> Reason for Leave</>}
          rules={[{ required: true, message: 'Please provide a reason' }]}
        >
          <TextArea
            rows={4}
            placeholder="Please provide detailed reason for your leave request..."
          />
        </Form.Item>

      </Form>
    </Modal>
  );
};

export default LeaveRequestForm;
